import React, { useState, useEffect } from 'react';
import { createOrUpdateFormFields, getFormFields } from '../../api/formField';
import { getActivityDetails } from '../../api/activities';
import Notification from '../../components/Notification';
import { useNavigate, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  MdAdd,
  MdDelete,
  MdSave,
  MdClose,
  MdDragIndicator,
  MdDescription,
  MdLibraryMusic
} from 'react-icons/md';

// Theme configuration
const THEMES = {
  event: {
    name: 'Event Management',
    primary: 'blue',
    gradient: 'from-blue-600 to-indigo-600',
    background: 'bg-gray-50',
    card: 'bg-white',
    text: 'text-gray-900',
    accent: 'text-blue-600',
    icon: MdDescription
  },
  fan: {
    name: 'Fan Management',
    primary: 'purple',
    gradient: 'from-purple-600 to-pink-600',
    background: 'bg-slate-50',
    card: 'bg-white',
    text: 'text-slate-900',
    accent: 'text-purple-600',
    icon: MdLibraryMusic
  }
};

// Field type options
const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'number', label: 'Number Input' },
  { value: 'datetime', label: 'Date & Time' },
  { value: 'dropdown', label: 'Dropdown Select' },
  { value: 'checkbox', label: 'Multiple Choice' },
  { value: 'image', label: 'Image Upload' }
];

// TagInput component for options
function TagInput({ value, onChange, placeholder }) {
  const [input, setInput] = useState('');

  const handleInput = (e) => setInput(e.target.value);

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === 'Tab') && input.trim()) {
      e.preventDefault();
      if (!value.includes(input.trim())) {
        onChange([...value, input.trim()]);
      }
      setInput('');
    } else if (e.key === 'Backspace' && !input && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  const removeTag = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border border-gray-300 rounded-lg p-3 min-h-[44px] focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
      {value.map((tag, idx) => (
        <div key={idx} className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
          <span className="font-medium">{tag}</span>
          <button
            type="button"
            className="ml-2 text-blue-600 hover:text-red-600 transition-colors duration-200"
            onClick={() => removeTag(idx)}
            aria-label="Remove option"
          >
            <MdClose className="w-4 h-4" />
          </button>
        </div>
      ))}
      <input
        className="flex-1 min-w-[120px] bg-transparent outline-none p-1 text-gray-900 placeholder-gray-500"
        type="text"
        value={input}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />
    </div>
  );
}

const FormManagement = () => {
  const navigate = useNavigate();
  const { activityId } = useParams();
  const [formData, setFormData] = useState([]);
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Determine theme based on activity type
  const theme = THEMES[activityData?.type] || THEMES.event;
  const ThemeIcon = theme.icon;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [formResponse, activityResponse] = await Promise.all([
          getFormFields(activityId),
          getActivityDetails(activityId)
        ]);
        
        // Ensure all fields have an id property
        const fieldsWithIds = (formResponse.data || []).map((field, index) => ({
          ...field,
          id: field.id || `existing_${index}_${Date.now()}`
        }));
        
        setFormData(fieldsWithIds);
        if (activityResponse?.success && activityResponse?.data) {
          setActivityData(activityResponse.data);
        }
        setError(null);
      } catch (err) {
        setError('Failed to fetch form data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activityId]);

  const handleAddField = () => {
    const newField = {
      id: `temp_${Date.now()}`,
      name: '',
      typeId: 'text',
      isRequired: false,
      options: []
    };
    setFormData([...formData, newField]);
  };

  const handleFieldChange = (fieldId, field, value) => {
    const newFields = formData.map((f) =>
      f.id === fieldId ? { ...f, [field]: value } : f
    );
    setFormData(newFields);
  };

  const handleOptionsChange = (fieldId, value) => {
    const newFields = formData.map((f) =>
      f.id === fieldId ? { ...f, options: value } : f
    );
    setFormData(newFields);
  };

  const handleDeleteField = (fieldId) => {
    const newFields = formData.filter((f) => f.id !== fieldId);
    setFormData(newFields);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      validateFormData(formData);
      const response = await createOrUpdateFormFields(activityId, formData);
      if (response.success) {
        setSuccess('Form configuration saved successfully');
        setError(null);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setError(`Failed to save form configuration: ${response.error}`);
        setSuccess(null);
      }
    } catch (err) {
      setError(`Failed to save form configuration: ${err.message}`);
      setSuccess(null);
      if (err.response && err.response.status === 401) {
        Cookies.remove('adminToken');
        navigate(`/admin/${activityId}/login`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const validateFormData = (formData) => {
    formData.forEach((field, index) => {
      if (!field.name.trim()) {
        throw new Error(`Field name is required for field ${index + 1}`);
      }
      
      // Special handling for Kota field - it doesn't need static options as it uses dynamic location data
      if (field.name === 'Kota' && field.typeId === 'dropdown') {
        return; // Skip options validation for Kota field
      }
      
      if ((field.typeId === 'dropdown' || field.typeId === 'checkbox') && (!field.options || field.options.length === 0)) {
        throw new Error(`Options are required for ${field.typeId} field: ${field.name}`);
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading form configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Notification
          type="error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Notification
          type="success"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4 mb-4">
          <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${theme.gradient} flex items-center justify-center`}>
            <ThemeIcon className="text-white text-xl" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Form Management</h1>
            <p className="text-gray-600">Configure registration form fields for your activity</p>
          </div>
        </div>

        {activityData && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-1">Activity: {activityData.name}</h3>
            <p className="text-sm text-gray-600">{theme.name}</p>
          </div>
        )}
      </div>

      {/* Form Fields Configuration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Form Fields</h2>
            <p className="text-gray-600 mt-1">Define the fields users will fill when registering</p>
          </div>
          
          <button
            type="button"
            onClick={handleAddField}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
          >
            <MdAdd className="mr-2" />
            Add Field
          </button>
        </div>

        <div className="space-y-4">
          {formData.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg font-medium mb-2">No form fields configured</p>
              <p>Click "Add Field" to create your first form field</p>
            </div>
          ) : (
            formData.map((field, index) => (
              <div
                key={field.id}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <MdDragIndicator className="text-gray-400" />
                  </div>
                  
                  <div className="flex-1 space-y-4">
                    {/* Field Name and Type */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="lg:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Field Name
                        </label>
                        <input
                          type="text"
                          placeholder="Enter field name (e.g., Full Name, Phone Number)"
                          value={field.name}
                          onChange={(e) => handleFieldChange(field.id, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Field Type
                        </label>
                        <select
                          value={field.typeId}
                          onChange={(e) => handleFieldChange(field.id, 'typeId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {FIELD_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Options for dropdown and checkbox */}
                    {(field.typeId === 'dropdown' || field.typeId === 'checkbox') && field.name !== 'Kota' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Options
                        </label>
                        <TagInput
                          value={field.options || []}
                          onChange={(value) => handleOptionsChange(field.id, value)}
                          placeholder="Type an option and press Enter to add"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Press Enter or Tab to add each option
                        </p>
                      </div>
                    )}

                    {/* Special note for Kota field */}
                    {field.name === 'Kota' && field.typeId === 'dropdown' && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-800">
                          <strong>Note:</strong> The Kota field uses dynamic location data (provinces and cities) 
                          and doesn't require manual options configuration.
                        </p>
                      </div>
                    )}

                    {/* Field Settings */}
                    <div className="flex items-center justify-between">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={field.isRequired}
                          onChange={(e) => handleFieldChange(field.id, 'isRequired', e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Required field</span>
                      </label>
                      
                      <button
                        type="button"
                        onClick={() => handleDeleteField(field.id)}
                        className="inline-flex items-center px-3 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      >
                        <MdDelete className="mr-1" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Save Button */}
        {formData.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors duration-200"
              >
                <MdSave className="mr-2" />
                {isSaving ? 'Saving...' : 'Save Form Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormManagement;